/**
 * FilterInput Component
 *
 * Renders individual filter inputs with consistent styling.
 * Handles different input types: select, text, date, datetime-local.
 */

import { type CSSProperties } from 'react';
import { Select } from '../Select';
import type { FilterConfig } from './types';

interface FilterInputProps {
  config: FilterConfig;
}

export function FilterInput({ config }: FilterInputProps) {
  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  };

  const labelStyle: CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: '#232730',
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    color: '#232730',
    backgroundColor: config.disabled ? '#F9FAFB' : '#FFFFFF',
    cursor: config.disabled ? 'not-allowed' : 'auto',
    outline: 'none',
    transition: 'all 200ms',
  };

  const handleChange = (value: string) => {
    config.onChange(value || undefined);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Convert datetime-local to ISO string
    if (config.type === 'datetime-local' && value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        handleChange(date.toISOString());
      }
    } else {
      handleChange(value);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!config.disabled) {
      e.currentTarget.style.borderColor = '#510CFF';
      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(81, 12, 255, 0.1)';
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!config.disabled) {
      e.currentTarget.style.borderColor = '#D1D5DB';
      e.currentTarget.style.boxShadow = 'none';
    }
  };

  return (
    <div style={containerStyle}>
      <label style={labelStyle} htmlFor={config.id}>
        {config.label}
      </label>

      {config.type === 'select' ? (
        <Select
          value={config.value}
          onChange={handleChange}
          options={config.options || []}
          disabled={config.disabled}
        />
      ) : (
        <input
          id={config.id}
          type={config.type}
          value={
            config.type === 'datetime-local' && config.value
              ? config.value.slice(0, 16)
              : config.value
          }
          onChange={handleInputChange}
          placeholder={config.placeholder}
          disabled={config.disabled}
          style={inputStyle}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-label={config.label}
        />
      )}
    </div>
  );
}
